import React from "react";
import { Instagram, ArrowUpRight } from "lucide-react";
import { INSTAGRAM_POSTS } from "@/config/social";

const BRAND_META = {
  dtf: {
    label: "Down To Film Apparel",
    profile: "https://www.instagram.com/downtofilmapparel_/",
    accent: "var(--dtf-blue)",
    tag: "@downtofilmapparel_",
  },
  sc: {
    label: "The Sticky Chick",
    profile: "https://www.instagram.com/the_sticky_chick/",
    accent: "var(--sc-pink)",
    tag: "@the_sticky_chick",
  },
};

function PostTile({ post, accent }) {
  return (
    <a
      href={post.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block border-2 border-black overflow-hidden bg-white"
      style={{ boxShadow: `4px 4px 0 0 ${accent}` }}
      data-testid={`ig-post-${post.image.split("/").pop()}`}
    >
      <div className="aspect-square overflow-hidden">
        <img
          src={post.image}
          alt={post.caption}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div
        className="absolute inset-0 bg-black/0 group-hover:bg-black/55 transition-colors flex items-end p-3"
      >
        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
          <p className="font-body text-white text-sm leading-tight">{post.caption}</p>
          <p className="label-mono text-white/70 mt-2 inline-flex items-center gap-1">
            View on Instagram <ArrowUpRight size={11} />
          </p>
        </div>
      </div>
      <div
        className="absolute top-2 right-2 w-7 h-7 inline-flex items-center justify-center border border-white/70 text-white bg-black/50 backdrop-blur-sm"
        aria-hidden
      >
        <Instagram size={13} />
      </div>
    </a>
  );
}

function BrandColumn({ brandKey }) {
  const meta = BRAND_META[brandKey];
  const posts = INSTAGRAM_POSTS[brandKey] || [];
  return (
    <div data-testid={`ig-feed-${brandKey}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <p className="label-mono" style={{ color: meta.accent }}>
            {meta.label}
          </p>
          <a
            href={meta.profile}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-sm hover:underline inline-flex items-center gap-1"
          >
            {meta.tag} <ArrowUpRight size={12} />
          </a>
        </div>
        <a
          href={meta.profile}
          target="_blank"
          rel="noopener noreferrer"
          className="label-mono border border-black px-3 py-2 hover:text-white transition-colors"
          style={{ "--h": meta.accent }}
          onMouseEnter={(e) => (e.currentTarget.style.background = meta.accent)}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          data-testid={`ig-follow-${brandKey}`}
        >
          Follow
        </a>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {posts.map((p, i) => (
          <PostTile key={i} post={p} accent={meta.accent} />
        ))}
      </div>
    </div>
  );
}

export default function InstagramFeed() {
  return (
    <section
      className="border-t-2 border-black bg-white"
      data-testid="instagram-feed-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="label-mono text-[color:var(--muted-text)]">
              FRESH OFF THE PRESS
            </p>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.9] mt-3">
              LATEST FROM<br />THE SHOP.
            </h2>
          </div>
          <p className="font-body text-[color:var(--muted-text)] max-w-sm">
            New work is dropping on Instagram every week. Peek at the last three
            jobs from each brand.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12">
          <BrandColumn brandKey="dtf" />
          <BrandColumn brandKey="sc" />
        </div>
      </div>
    </section>
  );
}
